/**
 * Resizes a data-URL image to fit within a max dimension and compresses as JPEG.
 * Used to keep Edge Function request bodies under size limits (~2 MB).
 */

const MAX_DIMENSION = 1280; // px — plenty for AI video input (720p)
const JPEG_QUALITY = 0.85;

/**
 * Downscale + compress a data-URL image.
 * Returns the original if it's already small enough (< threshold bytes).
 */
export async function resizeImageForUpload(
  dataUrl: string,
  maxBytes = 400_000,
  maxDimension = MAX_DIMENSION,
): Promise<string> {
  // Already a small data URL? Skip the work.
  // Blob URLs (blob:...) must always be processed to convert to data URL.
  if (dataUrl.startsWith('data:') && dataUrl.length < maxBytes) return dataUrl;

  return resizeAndEncode(dataUrl, maxDimension, JPEG_QUALITY);
}

/**
 * Downscale + re-encode a data-URL image to at most maxDim on the long edge, as JPEG.
 * Used to bound the drop-interim tile (attachDroppedImage) so undo snapshots never pin a
 * full-res string — the tile survives only until Unity's thumbnailChanged push replaces it.
 *
 * Fallback contract: ANY decode/canvas failure — including no canvas support at all, which
 * is the jsdom test environment — resolves with the ORIGINAL input, unchanged. Never rejects.
 */
export async function downscaleDataUrl(
  dataUrl: string,
  maxDim: number,
  quality: number,
): Promise<string> {
  if (typeof OffscreenCanvas === 'undefined') return dataUrl;
  try {
    return await resizeAndEncode(dataUrl, maxDim, quality);
  } catch {
    return dataUrl;
  }
}

/** Shared decode/canvas core for resizeImageForUpload and downscaleDataUrl. */
async function resizeAndEncode(dataUrl: string, maxDimension: number, quality: number): Promise<string> {
  const img = await loadImage(dataUrl);

  // Determine scale factor
  const scale = Math.min(1, maxDimension / Math.max(img.width, img.height));
  const w = Math.round(img.width * scale);
  const h = Math.round(img.height * scale);

  const canvas = new OffscreenCanvas(w, h);
  const ctx = canvas.getContext('2d')!;
  ctx.drawImage(img, 0, 0, w, h);

  const blob = await canvas.convertToBlob({ type: 'image/jpeg', quality });
  return blobToDataUrl(blob);
}

function loadImage(src: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = (_e) => reject(new Error('Failed to load image for resize'));
    img.src = src;
  });
}

function blobToDataUrl(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => resolve(reader.result as string);
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });
}
